import {
  CustomText,
  Header,
  Logo,
  MediaItem,
  Avatar,
  Position,
  Shape,
  Size,
  Stroke,
  SubtitlesItem,
  Table,
  TemplateState,
  TitleItem,
  NullableHeightSize,
  ParameteredText,
  TableDividerStyle,
  TableTextStyle,
} from "../types";

export const adjustTemplateToAspectRatioChange = (
  template: TemplateState,
  newAspectRatio: number,
  previewSize: Size
): TemplateState => {
  const oldPreviewWidth = previewSize.width;
  const newPreviewWidth = Math.round(previewSize.height * newAspectRatio);
  const scale = oldPreviewWidth / newPreviewWidth;

  return {
    ...template,
    header: adjustItem<Header>(template.header, scale),
    textStyle: adjustItem<SubtitlesItem>(template.textStyle, scale),
    titleStyle: adjustItem<TitleItem>(template.titleStyle, scale),
    presenter: adjustItem<Avatar>(template.presenter, scale),
    logo: adjustItem<Logo>(template.logo, scale),
    images: template.images.map((image) => adjustItem<MediaItem>(image, scale)),
    videos: template.videos.map((video) => adjustItem<MediaItem>(video, scale)),
    shapes: template.shapes.map((shape) => adjustItem<Shape>(shape, scale)),
    customTexts: template.customTexts.map((text) =>
      adjustItem<CustomText>(text, scale)
    ),
    parameteredTexts: template.parameteredTexts?.map((text) =>
      adjustItem<ParameteredText>(text, scale)
    ),
    mainImage: template.mainImage
      ? adjustItem<MediaItem>(template.mainImage, scale)
      : undefined,
    figure: template.figure
      ? {
          number: adjustItem<ParameteredText>(template.figure.number, scale),
          units: template.figure.units
            ? adjustItem<ParameteredText>(template.figure.units, scale)
            : undefined,
        }
      : undefined,
    slideLayoutShapes: template.slideLayoutShapes?.map((shape) =>
      adjustItem<Shape>(shape, scale)
    ),
    table: template.table
      ? {
          ...adjustItem<Table>(template.table, scale),
          textStyle: {
            ...adjustItem<TableTextStyle>(template.table.textStyle, scale),
            // size/position are relative to cell, not preview — restore after adjustItem scales fontSize/stroke
            size: template.table.textStyle.size,
            position: template.table.textStyle.position,
          },
          dividerStyle: {
            ...adjustItem<TableDividerStyle>(
              template.table.dividerStyle,
              scale
            ),
            // size/position are relative to cell, not preview — restore after adjustItem scales fontSize/stroke
            size: template.table.dividerStyle.size,
            position: template.table.dividerStyle.position,
          },
        }
      : undefined,
  };
};

const adjustPositionRelativeToCenter = (
  position: Position,
  size: Size | NullableHeightSize,
  scale: number
): Position => {
  const itemCenterX = position.x + size.width / 2;
  const offsetFromPreviewCenterX = 0.5 - itemCenterX;

  const newOffsetX = offsetFromPreviewCenterX * scale;
  const newWidth = size.width * scale;

  let newX = Math.max(0, 0.5 - newOffsetX - newWidth / 2);
  if (newX + newWidth > 1) {
    newX = 1 - newWidth;
  }

  return { x: newX, y: position.y };
};

const adjustItem = <
  T extends {
    position: Position;
    size: Size | NullableHeightSize;
    fontSize?: number;
    stroke?: Stroke;
  },
>(
  item: T,
  scale: number
): T => {
  // Items spanning the full preview width (width === 1, x === 0) should
  // keep their dimensions and position after an aspect ratio change.
  // fontSize and stroke still scale so text remains visually proportional.
  const isFullWidth = item.size.width === 1;

  return {
    ...item,
    size: {
      ...item.size,
      width: isFullWidth ? item.size.width : item.size.width * scale,
    },
    position: isFullWidth
      ? item.position
      : adjustPositionRelativeToCenter(item.position, item.size, scale),
    ...(item.fontSize
      ? {
          fontSize: Math.round(item.fontSize * scale),
        }
      : {}),
    ...(item.stroke
      ? {
          stroke: {
            ...item.stroke,
            width: (item.stroke.width ?? 0) * scale,
          },
        }
      : {}),
  };
};
